import { Api, APIResult } from 'utils/Api';
import {
  all,
  call,
  put,
  CallEffect,
  PutEffect,
  AllEffect,
} from 'redux-saga/effects';
import { types as contributorTypes } from 'reducers/Contributors';
import { types as billingTypes } from 'reducers/billing';
import { Contributor } from 'models/Contributors';

export interface Action {
  type: string;
  contributor?: Contributor;
  toggle?: boolean;
  add?: boolean;
  organizationUUID?: string;
}

export function* fetchContributors(
  action: Action
): Generator<CallEffect | PutEffect, void, APIResult> {
  const { organizationUUID } = action;
  if (!organizationUUID) {
    throw new Error('Missing required parameters for contributor list.');
  }

  let contributors: Contributor[] = [];
  let page = 1;
  while (true) {
    const response = yield call(
      Api.get,
      `organizations/${encodeURIComponent(
        organizationUUID
      )}/billing/contributors?${new URLSearchParams({ page: String(page) })}`
    );

    if (!response.success) {
      yield put({
        type: contributorTypes.GET_CONTRIBUTORS_FAILURE,
        error: response.data?.error_message || 'Unknown error',
        errorCode: response.status,
      });
      return;
    }

    const newContributors = response.data.data;
    contributors = [...contributors, ...newContributors];

    if (!response.data.next_page_url) {
      break;
    }
    page += 1;
  }

  yield put({
    type: contributorTypes.GET_CONTRIBUTORS_SUCCESS,
    uuid: organizationUUID,
    contributors,
  });
}

export function* updateContributors(
  action: Action
): Generator<CallEffect | PutEffect | AllEffect<PutEffect>, void, APIResult> {
  const { organizationUUID, contributor, add } = action;
  if (!organizationUUID || !contributor) {
    throw new Error('Missing required parameters for updating contributors.');
  }

  const response = yield call(
    Api.put,
    `organizations/${organizationUUID}/billing/contributors`,
    {
      add,
      name: contributor.username,
      outside_collaborator: contributor.outside_collaborator,
    }
  );

  if (!response.success) {
    yield put({
      type: contributorTypes.UPDATE_CONTRIBUTORS_FAILURE,
      error: response.data?.error_message || 'Unknown error',
      errorCode: response.status,
    });
  } else {
    yield all([
      put({
        type: contributorTypes.UPDATE_CONTRIBUTORS_SUCCESS,
        contributor,
      }),
      put({
        type: contributorTypes.GET_CONTRIBUTORS_REQUEST,
        organizationUUID,
      }),
      put({
        type: billingTypes.GET_BILLING_INFO_REQUEST,
        organizationUUID,
      }),
    ]);
  }
}
